import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Autocomplete } from '@material-ui/lab';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    Typography,
} from '@material-ui/core';
import {
    CreateHub,
    EditHub,
    GetBluedartStoreZone,
    GetBluedartZones,
} from '../../store/actions/settingsActionCreator';
import { showMessage } from '../../utils/helper';

interface BluedartHubModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    hub?: any;
    storeId?: string;
    zone?: string;
}

const phoneRegExp = /^[0-9]{10}$/;

/**
 * Single Add Hub for Blue Dart (PRD RB-9 / decision #19 — only bulk upload existed, single-add is
 * needed for one-off hubs). Available to Super Admin and Regional Approver; the caller is gated on
 * the hub_master permission and the backend independently validates the store's resolved zone
 * against the creator's own, rejecting out-of-zone hubs.
 *
 * <p>Deliberately NOT built on HubModal: that form carries geofence radius, platform type,
 * org-customer binding, dedicated slots, referral program and a map picker, none of which apply to
 * a BD hub — and it lacks the two fields that matter here, store id and vendor code. The fields
 * below mirror the BD bulk-upload columns, which is the shape the backend already validates.
 */
const hubSchema = yup.object({
    bluedartUniqueStoreId: yup.string().trim().required('Store ID is required'),
    // Required only for a store we have never seen; see knownZone below.
    bluedartZone: yup.string().trim(),
    vendorUniqueId: yup.string().trim().required('Vendor is required'),
    name: yup.string().trim().required('Hub Name is required'),
    address: yup.string().trim().required('Address is required'),
    // Optional: empty for every hub in production, and the backend never reads it. Still
    // validated when supplied, so a half-typed number cannot be saved.
    contactNumber: yup.string().matches(phoneRegExp, {
        message: 'Contact Number must be 10 digits',
        excludeEmptyString: true,
    }),
    latitude: yup
        .number()
        .typeError('Latitude must be a number')
        .min(-90)
        .max(90)
        .required('Latitude is required'),
    longitude: yup
        .number()
        .typeError('Longitude must be a number')
        .min(-180)
        .max(180)
        .required('Longitude is required'),
    cityName: yup.string().trim().required('City is required'),
    // Optional: never enforced server-side, and the bulk upload accepts a blank.
    stateName: yup.string().trim(),
});

const editHubSchema = hubSchema.shape({
    bluedartUniqueStoreId: yup.string().trim(),
    vendorUniqueId: yup.string().trim(),
});

const BluedartHubModal: React.FC<BluedartHubModalProps> = ({
    open,
    onClose,
    onSuccess,
    hub,
    storeId: hubStoreId,
    zone,
}) => {
    const isEdit = !!hub?.id;
    const dispatch = useDispatch();
    const [submitting, setSubmitting] = useState(false);
    // null = not looked up yet or the store is new; a string = the zone the store already has, in
    // which case the creator does not get to choose one.
    const [knownZone, setKnownZone] = useState<string | null>(null);
    const [checkingStore, setCheckingStore] = useState(false);
    // Zones already in use. Suggestions, not a closed list -- see the Zone field below.
    const [zones, setZones] = useState<string[]>([]);

    // Same list the rider filters use — already narrowed server-side to vendors operating at the
    // caller's in-scope stores.
    const vendors = useSelector(
        (state: any) => state?.staff?.bdFilterVendors ?? [],
    );

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            bluedartUniqueStoreId: isEdit ? (hubStoreId ?? '') : '',
            bluedartZone: isEdit ? (zone ?? '') : '',
            // Not returned by the store drill-down: the hub already belongs to its vendor org and
            // the update path never re-binds it, so there is nothing to prefill or change.
            vendorUniqueId: '',
            name: hub?.name ?? '',
            address: hub?.address ?? '',
            contactNumber: hub?.contactNumber ?? '',
            latitude: hub?.latitude != null ? String(hub.latitude) : '',
            longitude: hub?.longitude != null ? String(hub.longitude) : '',
            cityName: hub?.cityName ?? '',
            stateName: hub?.stateName ?? '',
        },
        validationSchema: isEdit ? editHubSchema : hubSchema,
        onSubmit: (values) => {
            if (isEdit) {
                setSubmitting(true);
                dispatch(
                    EditHub(
                        {
                            hubs: [
                                {
                                    id: hub.id,
                                    hubName: values.name,
                                    name: values.name,
                                    address: values.address,
                                    // contactNumber is not sent: the Hubs entity does not map
                                    // hubs.contact_number, so BeanUtils.copyProperties drops it
                                    // and the field would look like it saved when it did not.
                                    latitude: Number(values.latitude),
                                    longitude: Number(values.longitude),
                                    cityName: values.cityName,
                                    stateName: values.stateName,
                                },
                            ],
                        },
                        () => {
                            setSubmitting(false);
                            showMessage('Hub updated', 'success');
                            onSuccess?.();
                            onClose();
                        },
                        (error: any) => {
                            setSubmitting(false);
                            // "Rejected - hub outside your scope." and the read-only refusal come
                            // back verbatim, so the editor sees which rule stopped them.
                            showMessage(
                                error?.status?.message ??
                                    'Could not update hub',
                                'error',
                            );
                        },
                    ),
                );
                return;
            }
            // Enforced here rather than in the yup schema because it depends on the lookup result,
            // not on another field. The API applies the same rule, so this is only to save a round
            // trip and point at the field.
            if (!knownZone && !values.bluedartZone?.trim()) {
                showMessage(
                    'This store is new, so its zone is required.',
                    'error',
                );
                return;
            }
            setSubmitting(true);
            dispatch(
                CreateHub(
                    {
                        hubs: [
                            {
                                ...values,
                                hubName: values.name,
                                latitude: Number(values.latitude),
                                longitude: Number(values.longitude),
                                isActive: true,
                            },
                        ],
                    },
                    () => {
                        setSubmitting(false);
                        showMessage('Hub created', 'success');
                        onSuccess?.();
                        onClose();
                    },
                    (error: any) => {
                        setSubmitting(false);
                        // The backend returns "Rejected - hub outside your zone." and the
                        // no-zone-mapping message verbatim; surfacing it tells the creator which
                        // of the two happened.
                        showMessage(
                            error?.status?.message ?? 'Could not create hub',
                            'error',
                        );
                    },
                ),
            );
        },
    });

    useEffect(() => {
        if (!open) {
            return;
        }
        dispatch(
            GetBluedartZones(
                (response: any) => setZones(response?.response?.zones ?? []),
                // A failed lookup only costs the suggestions; the field still accepts a typed zone.
                () => setZones([]),
            ),
        );
    }, [dispatch, open]);

    // Looked up as the store id is typed. Debounced because the field is free text and every
    // keystroke would otherwise hit the API; 400ms is long enough that a pasted id costs one call.
    const storeId = formik.values.bluedartUniqueStoreId?.trim();
    useEffect(() => {
        // Edit mode knows the store and its zone from the drill-down that opened this, and the
        // store field is locked — so there is nothing to look up and nothing that could change it.
        if (isEdit) {
            return;
        }
        if (!storeId) {
            setKnownZone(null);
            return;
        }
        setCheckingStore(true);
        const timer = setTimeout(() => {
            dispatch(
                GetBluedartStoreZone(
                    storeId,
                    (response: any) => {
                        setCheckingStore(false);
                        setKnownZone(response?.response?.zone ?? null);
                    },
                    () => {
                        // Treated as "unknown store", so the creator is asked for a zone rather
                        // than blocked. The API refuses a wrong one anyway.
                        setCheckingStore(false);
                        setKnownZone(null);
                    },
                ),
            );
        }, 400);
        return () => clearTimeout(timer);
    }, [dispatch, storeId, isEdit]);

    // Read off the schema rather than a hand-kept list, so the asterisk cannot drift out of
    // sync with what is actually enforced.
    const isRequired = (name: string) =>
        ((hubSchema.describe().fields as any)?.[name]?.tests ?? []).some(
            (t: any) => t?.name === 'required',
        );

    const field = (
        name: keyof typeof formik.values,
        label: string,
        disabled = false,
    ) => (
        <TextField
            fullWidth
            size="small"
            variant="outlined"
            disabled={disabled}
            name={name}
            label={
                isRequired(name as string) ? (
                    <>
                        {label}
                        <span style={{ color: '#c62828' }}> *</span>
                    </>
                ) : (
                    label
                )
            }
            value={formik.values[name]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            // Narrowed explicitly: validationSchema is picked between two schemas, which widens
            // the inferred error type beyond what helperText accepts.
            helperText={
                formik.touched[name] && formik.errors[name]
                    ? String(formik.errors[name])
                    : undefined
            }
        />
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <form onSubmit={formik.handleSubmit}>
                <DialogTitle>
                    {isEdit ? 'Edit Hub' : 'Add Hub'}
                    <Typography variant="body2" color="textSecondary">
                        {isEdit
                            ? 'Store, zone and vendor belong to the store and cannot be changed here.'
                            : 'A store already in use keeps its zone; a new one needs to be given one.'}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            {field('bluedartUniqueStoreId', 'Store ID', isEdit)}
                        </Grid>
                        <Grid item xs={6}>
                            {/* A store already in use has exactly one zone and it is not the
                                creator's to change, so the field goes read-only and shows it.
                                Only a store nobody has seen before is asked for one. */}
                            <Autocomplete
                                // freeSolo: a zone holds no hub until its first one exists, so it
                                // is absent from the list until then. Refusing an unlisted value
                                // would make the first hub of a new zone uncreatable.
                                freeSolo
                                options={zones}
                                disabled={
                                    isEdit || !!knownZone || checkingStore
                                }
                                value={knownZone ?? formik.values.bluedartZone}
                                onChange={(_e: any, picked: any) =>
                                    formik.setFieldValue(
                                        'bluedartZone',
                                        (picked ?? '') as string,
                                    )
                                }
                                onInputChange={(
                                    _e: any,
                                    text: string,
                                    reason: string,
                                ) => {
                                    if (reason !== 'input') return;
                                    // Upper-cased because a typed "south" would otherwise become a
                                    // zone distinct from SOUTH, splitting scope with nothing to
                                    // show for it -- the column is compared as a plain string.
                                    formik.setFieldValue(
                                        'bluedartZone',
                                        text.toUpperCase(),
                                    );
                                }}
                                renderInput={(params: any) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        name="bluedartZone"
                                        label={
                                            knownZone ? (
                                                'Zone'
                                            ) : (
                                                <>
                                                    Zone{' '}
                                                    <span
                                                        style={{
                                                            color: '#d32f2f',
                                                        }}
                                                    >
                                                        *
                                                    </span>
                                                </>
                                            )
                                        }
                                        helperText={
                                            checkingStore
                                                ? 'Checking store…'
                                                : knownZone
                                                  ? 'Taken from the store, which is already mapped.'
                                                  : 'This store is new — pick a zone or type a new one.'
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        {isEdit ? (
                            // Shown, not editable. A hub belongs to its vendor org and the update
                            // path never re-binds it, so a picker here would be a control with no
                            // effect. The drill-down returns the name but not the code, which is
                            // what the picker below selects on.
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    disabled
                                    label="Vendor"
                                    value={hub?.vendorName ?? '-'}
                                />
                            </Grid>
                        ) : (
                            <Grid item xs={6}>
                                <Autocomplete
                                    options={vendors}
                                    getOptionLabel={(v: any) =>
                                        `${v?.name ?? ''}${
                                            v?.vendorCode
                                                ? ` (${v.vendorCode})`
                                                : ''
                                        }`
                                    }
                                    getOptionSelected={(v: any, picked: any) =>
                                        v?.vendorCode === picked?.vendorCode
                                    }
                                    // vendorUniqueId holds the vendor CODE, so the selected option is
                                    // resolved back from the list rather than held as its own object.
                                    value={
                                        vendors.find(
                                            (v: any) =>
                                                v?.vendorCode ===
                                                formik.values.vendorUniqueId,
                                        ) ?? null
                                    }
                                    onChange={(_e: any, picked: any) =>
                                        formik.setFieldValue(
                                            'vendorUniqueId',
                                            picked?.vendorCode ?? '',
                                        )
                                    }
                                    onBlur={() =>
                                        formik.setFieldTouched(
                                            'vendorUniqueId',
                                            true,
                                        )
                                    }
                                    renderInput={(params: any) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            name="vendorUniqueId"
                                            placeholder="Search vendor or code"
                                            label={
                                                <>
                                                    Vendor
                                                    <span
                                                        style={{
                                                            color: '#c62828',
                                                        }}
                                                    >
                                                        {' '}
                                                        *
                                                    </span>
                                                </>
                                            }
                                            error={
                                                formik.touched.vendorUniqueId &&
                                                Boolean(
                                                    formik.errors
                                                        .vendorUniqueId,
                                                )
                                            }
                                            helperText={
                                                formik.touched.vendorUniqueId &&
                                                formik.errors.vendorUniqueId
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            {field('name', 'Hub Name')}
                        </Grid>
                        <Grid item xs={12}>
                            {field('address', 'Address')}
                        </Grid>
                        <Grid item xs={6}>
                            {field('cityName', 'City')}
                        </Grid>
                        <Grid item xs={6}>
                            {field('stateName', 'State')}
                        </Grid>
                        <Grid item xs={6}>
                            {field('latitude', 'Latitude')}
                        </Grid>
                        <Grid item xs={6}>
                            {field('longitude', 'Longitude')}
                        </Grid>
                        {/* Add only. hubs.contact_number is not mapped on the Hubs entity, so an
                            edit can neither prefill it nor save it — showing the field would make
                            it look like it had. Left on the add form as it was. */}
                        {!isEdit && (
                            <Grid item xs={6}>
                                {field('contactNumber', 'Contact Number')}
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={!formik.isValid || submitting}
                    >
                        {submitting ? (
                            <CircularProgress size={18} />
                        ) : isEdit ? (
                            'Save Changes'
                        ) : (
                            'Add Hub'
                        )}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default BluedartHubModal;
